// Id-keyed dedup merge for reconnect catch-up (CHAT-08, D-36).
//
// `mergeById` is a pure function (no socket, no DOM) that upserts an array of messages
// (from a 'history' WS refetch or a live 'message' event) into an id-keyed
// `Map<number, ChatMessage>`, returning a stably-sorted array ordered by `createdAt`
// then `id`. Re-delivering the SAME id never creates a duplicate (last write wins);
// an out-of-order (older) id arriving later still sorts into its correct position.
//
// This is one of TWO identical pure-function copies (D-36/04-03-PLAN) — the other lives
// at apps/portal/src/lib/mergeById.ts. No cross-package dependency: keep both copies in
// sync if the contract changes.

/** Minimal shape mergeById depends on — the widget ChatMessage type satisfies this
 * (structural typing, no index signature so richer shapes remain assignable). */
export interface ChatMessage {
    id: number;
    createdAt: string;
    content: string;
}

/**
 * Upserts `incoming` into `map` by `id` (mutates `map` in place) and returns a
 * stably-sorted array of all values, ordered by `createdAt` ascending then `id`
 * ascending (Pattern 4 tiebreak — messages created within the same millisecond).
 */
export function mergeById<T extends ChatMessage>(map: Map<number, T>, incoming: T[]): T[] {
    for (const msg of incoming) {
        map.set(msg.id, msg);
    }

    return Array.from(map.values()).sort((a, b) => {
        const byCreatedAt = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        return byCreatedAt !== 0 ? byCreatedAt : a.id - b.id;
    });
}
